import React, { Component, useEffect, useState } from "react";
import classNames from "classnames";

import * as Style from "./style";
import { NavigationType } from "./data";
import { useAuthUser } from "hooks/useAuthUser";
import {
  environmentOptions,
  // getCurrentModule,
  getLocalStorageByPrefix,
} from "utils/helpers";
import { useHistory } from "react-router-dom";
import { isActiveSubPath } from "./activeSubPath";

const CHILD_HEIGHT = 33; // Height of each saved search item
const GROUP_HEADER_HEIGHT = 34; // Height of the group header
const GROUP_TOP_MARGIN = 10; // Top margin for groups after the first one
const MAX_HEIGHT = 350; // Maximum height for the navigation list
const NO_RESULTS_HEIGHT = 33; // Height for the "no saved searches" message

// Map of search types to their corresponding paths
const TYPE_TO_PATH = {
  articles: '/editorial/articles',
  products: '/editorial/products',
  // Add more mappings as needed
};

/**
 * @typedef {Object} SavedSearchItem
 * @property {string} key
 * @property {string} name
 * @property {string} data
 */

/**
 * @typedef {Object} GroupedSearchType
 * @property {string} type
 * @property {SavedSearchItem[]} items
 */

/**
 * @typedef {Object.<string, GroupedSearchType>} GroupedSearches
 */

const SavedSearchChildren = ({ className }) => {
  const [data, setData] = useState({});
  const { userId, error: userError, loading: userLoading } = useAuthUser();
  const envOptions = environmentOptions();
  const history = useHistory();

  const getFixedPartOfKey = (usrId) => {
    return `Search:${envOptions.X_ENV}:${
      envOptions.X_HOST_SHORTNAME
    }:${usrId}:`;
  };

  const fetchAndSetLocalStorageData = (usrId) => {
    const fixedPartOfKey = getFixedPartOfKey(usrId);
    const savedSearches = getLocalStorageByPrefix(fixedPartOfKey);
    setData(savedSearches);
  };

  const onClickHandler = (val, usrId) => {
    if (!val) return;
    
    try {
      const savedSearches = getLocalStorageByPrefix(getFixedPartOfKey(usrId));
      const newKey = val.replace(getFixedPartOfKey(usrId), "");
      const queryData = savedSearches[val]["data"];
      
      // Extract the type from the key
      const parts = val.split(':');
      if (parts.length < 5) return;
      
      const type = parts[4];
      const pathname = TYPE_TO_PATH[type] || location.pathname;

      history.replace({
        pathname,
        search: `k=${newKey}&q=${queryData}`,
      });
    } catch (e) {
      console.error('Error handling saved search click:', e);
    }
  };

  useEffect(() => {
    const handleSaveSearch = (e) => {
      fetchAndSetLocalStorageData(e.detail.userId);
      onClickHandler(e.detail.key, e.detail.userId);
    };

    window.addEventListener("local-storage-save-search", handleSaveSearch);
    return () => window.removeEventListener("local-storage-save-search", handleSaveSearch);
  }, []);

  useEffect(() => {
    const handleResetSearch = () => {
      history.replace({
        pathname: location.pathname,
        search: "",
      });
    };

    window.addEventListener("local-storage-save-search-reset", handleResetSearch);
    return () => window.removeEventListener("local-storage-save-search-reset", handleResetSearch);
  }, [history]);

  useEffect(() => {
    if (userError || userLoading) return;
    fetchAndSetLocalStorageData(userId);
  }, [userError, userLoading, userId]);

  // Group searches by type
  const groupSearches = () => {
    /** @type {GroupedSearches} */
    const grouped = {};
    
    Object.entries(data).forEach(([key, value]) => {
      const parts = key.split(':');
      if (parts.length < 5) return;
      
      const type = parts[4];
      if (!grouped[type]) {
        grouped[type] = {
          type,
          items: []
        };
      }
      
      grouped[type].items.push({
        key,
        name: value.name,
        data: value.data
      });
    });

    Object.values(grouped).forEach(group => {
      group.items.sort((a, b) => a.name.localeCompare(b.name));
    });

    return grouped;
  };

  const groupedSearches = groupSearches();
  const groups = Object.values(groupedSearches);
  const hasSearches = groups.length > 0;
  
  // Calculate heights
  const itemsHeight = hasSearches 
    ? groups.reduce((sum, group) => sum + (group.items.length * CHILD_HEIGHT), 0)
    : NO_RESULTS_HEIGHT;
  const headersHeight = groups.length * GROUP_HEADER_HEIGHT;
  const marginHeight = (groups.length - 1) * GROUP_TOP_MARGIN;
  const totalHeight = Math.min(itemsHeight + headersHeight + marginHeight, MAX_HEIGHT);
  const shouldScroll = itemsHeight + headersHeight + marginHeight > MAX_HEIGHT;

  return (
    <Style.SubNavListWrapper
      className={className}
      childHeight={totalHeight}
      style={{ 
        overflowY: shouldScroll ? "auto" : "hidden",
        maxHeight: MAX_HEIGHT
      }}
    >
      <Style.SubNavList>
        {hasSearches ? (
          Object.entries(groupedSearches).map(([type, group]) => (
            <Style.LISub key={type}>
              <Style.GroupHeader>{type}</Style.GroupHeader>
              {group.items.map((item) => (
                <Style.SavedSearchLink
                  key={item.key}
                  value={item.key}
                  onClick={() => onClickHandler(item.key, userId)}
                >
                  <span style={{textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', display: 'block'}}>{item.name}</span>
                </Style.SavedSearchLink>
              ))}
            </Style.LISub>
          ))
        ) : (
          <Style.LISub>
            <Style.SavedSearchLink disabled>
              <em>No saved searches found</em>
            </Style.SavedSearchLink>
          </Style.LISub>
        )}
      </Style.SubNavList>
    </Style.SubNavListWrapper>
  );
};

class SubLinks extends Component {
  getSubListHeight = (parent) => {
    const childrenCount = parent.children?.length || 0;
    const rawHeight = childrenCount * CHILD_HEIGHT;
    return Math.min(rawHeight, MAX_HEIGHT);
  };

  render() {
    const { parentIndex, parent, openParentID } = this.props;
    const accordionClass = classNames({
      "-is-expanded": openParentID === parentIndex,
    });

    if (parent.type === NavigationType.SAVED_SEARCHES) {
      return (
        <SavedSearchChildren className={accordionClass} />
      );
    }

    const rawHeight = (parent.children?.length || 0) * CHILD_HEIGHT;
    const shouldScroll = rawHeight > MAX_HEIGHT;
    const siblingPaths = parent.children?.map((child) => parent.to + child.to) || [];

    return (
      <Style.SubNavListWrapper
        className={accordionClass}
        childHeight={this.getSubListHeight(parent)}
        style={{ 
          overflowY: shouldScroll ? "auto" : "hidden",
          maxHeight: MAX_HEIGHT
        }}
      >
        <Style.SubNavList>
          {parent.children?.map((child, index) => {
            const to = parent.to + child.to;
            return (
              <Style.LISub key={index}>
                <Style.SubLink
                  id={child.text?.toKebabCase()}
                  to={to}
                  // Every sibling's path, so the decision is which child owns
                  // this location rather than whether this one could. See
                  // activeSubPath.ts: one child's path is the prefix of
                  // another's, and NavLink's own prefix match lights up both.
                  isActive={(_match, location) =>
                    isActiveSubPath(location.pathname, to, siblingPaths)
                  }
                >
                  {child.text}
                </Style.SubLink>
              </Style.LISub>
            );
          })}
        </Style.SubNavList>
      </Style.SubNavListWrapper>
    );
  }
}

export default SubLinks;
